import { useState } from "react";
import { Icon } from "@mui/material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import { Success, Error, MessageDialog } from "ui-components/Alert";
import BasicTabs from "ui-components/BasicTabs";
import { ListLayout, TableCard } from "ui-components/Layout";
import BasicTable from "ui-components/tables/BasicTable";
import { EditButton, DelButton, ActiveButton } from "ui-components/IconButton";
import { getDivisionList, deactivateDivision, activateDivision, getDivisionStatusCount } from "services/data.internal.service";
import { useTeamValue } from "providers/TeamContext";
import { useCustomerValue } from "providers/CustomerContext";

function DivisionList() {
  const navigate = useNavigate();
  const { permissions, allowAll } = useTeamValue();
  const { customerCode } = useCustomerValue();
  const [alert, setAlert] = useState(null);
  const [dialog, setDialog] = useState(null);
  const [data, setData] = useState([]);
  const [status, setStatus] = useState("Active");
  const active = status === "Active";
  const [tabs, setTabs] = useState([{ label: "Active", count: 0 }, { label: "InActive", count: 0 }]);
  const queryClient = useQueryClient();
  const onSuccess = (data) => {
    queryClient.invalidateQueries(["division-list"]);
    setAlert(<Success data={data} onChange={setAlert} />);
  };
  const onError = (data) => setAlert(<Error data={data} onChange={setAlert} />);
  const addPermission = allowAll || (Boolean(permissions["Division"]) && permissions["Division"].includes("new"));
  const editPermission = allowAll || (Boolean(permissions["Division"]) && permissions["Division"].includes("edit"));
  const deactivatePermission = allowAll || (Boolean(permissions["Division"]) && permissions["Division"].includes("deactivate"));

  useQuery({
    queryKey: ["division-list", { customerCode, active }],
    queryFn: () => getDivisionList({ active, customerCode }),
    onSuccess: (data) => setData(data.data.data),
    onError,
  });

  useQuery({
    queryKey: ["division-list", "status", { customerCode }],
    queryFn: () => getDivisionStatusCount({ customerCode }),
    onSuccess: (data) => setTabs(data.data.data),
    onError,
  });

  const delMutation = useMutation(deactivateDivision, { onSuccess, onError });
  const activeMutation = useMutation(activateDivision, { onSuccess, onError });

  const onNew = () => navigate("/division/add");
  const onEdit = (req) => navigate("/division/add", { state: req });
  const handleClose = () => setDialog(null);
  const handleOk = (req) => {
    delMutation.mutate(req);
    setDialog(null);
  };
  const onDelete = (req) => {
    setDialog(
      <MessageDialog title={`Deactivate ${req.divisionGroup}`} onClose={handleClose} onOk={() => handleOk({ ...req, customerCode })} />
    );
  };
  const onActive = (req) => {
    setDialog(
      <MessageDialog
        title={`Activate ${req.divisionGroup}`}
        onClose={() => setDialog(null)}
        onOk={() => {
          activeMutation.mutate({ ...req, customerCode });
          setDialog(null);
        }}
      />
    );
  };

  const columns = [
    { header: "Division Group", accessorKey: "divisionGroup" },
    { header: "Description", accessorKey: "divisionDesc1" },
    { header: "Customer Code", accessorKey: "customerCode" },
    // { header: "Short Name", accessorKey: "shortName" },
    // { header: "Status", accessorKey: "status" },
    { header: "Action", accessorKey: "action",
      enableColumnFilter: false,
      cell: ({ row }) => {
        const { divisionGroup } = row.original;
        if (active) {
          return (
            <>
              <EditButton title="Edit Division" disabled={!editPermission} onClick={() => onEdit(row.original)} />
              <DelButton title="Deactivate Division" disabled={!deactivatePermission} onClick={() => onDelete({ divisionGroup })} />
            </>
          );
        } else {
          return <ActiveButton title="Activate Division" disabled={!editPermission} onClick={() => onActive({ divisionGroup })} />;
        }
      },
    },
  ];

  return (
    <ListLayout>
      {dialog}
      <MDBox my={3}>{alert}<BasicTabs tabs={tabs} status={status} setStatus={setStatus} /></MDBox>
      <TableCard>
        <MDBox display="flex" justifyContent="space-between" margin="10px">
          <MDButton variant="gradient" color="info" onClick={onNew} disabled={!addPermission}>
            <Icon>add</Icon>&nbsp;Division&nbsp;
          </MDButton>
        </MDBox>
        <BasicTable columns={columns} data={data} />
      </TableCard>
    </ListLayout>
  );
}

export default DivisionList;